import { FormNotice, theme } from '@zappyrent/hundop';
import { addDays, format, isBefore } from 'date-fns';
import { debounce, upperFirst } from 'lodash';
import { Dispatch, SetStateAction } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';

import { useToastContext } from '../../../contexts/ToastProvider';
import bookingsAPI from '../../../services/api/bookings';
import { numberValidationPattern } from '../../../shared/helpers';
import { BookingCounterOfferForm } from '../../../shared/ts/booking';
import useStore from '../../../store';
import { LandlordBooking, Status } from '../../../types/bookings';
import TextInput from '../../___design/TextInput/TextInput';
import Modal from '../../Modal/Modal';
import ModalBody from '../../Modal/ModalBody/ModalBody';
import ModalContent from '../../Modal/ModalContent/ModalContent';
import ModalFooter from '../../Modal/ModalFooter/ModalFooter';
import ModalHeader from '../../Modal/ModalHeader/ModalHeader';
import {
  StyleButtonWrapper,
  StyledButton,
  StyledGrid,
  StyledSpinner,
} from './BookingCounterOfferModal.styles';

const BookingCounterOfferModal = ({
  onClose,
  booking,
  setBookings,
}: {
  onClose: () => void;
  booking: LandlordBooking;
  setBookings: Dispatch<SetStateAction<LandlordBooking[]>>;
}) => {
  const { t } = useTranslation([
    'booking',
    'common',
    'form',
    'formError',
    'error',
  ]);
  const toast = useToastContext();
  const defaultFormErrorLabel = t('form:requiredField');
  const { user } = useStore(state => ({
    user: state.user,
  }));

  const { mutateAsync, isLoading } = useMutation(
    bookingsAPI.counterOfferLandlordBooking,
    {
      onSuccess: () => {
        toast.success(upperFirst(t('booking:counterOfferBookingSuccess')));
        setBookings(prevValue => {
          const newBooking = [...prevValue];
          const bookingIndex = newBooking.findIndex(
            preValBooking => preValBooking.id === booking.id,
          );
          newBooking.splice(bookingIndex, 1, {
            ...booking,
            pricing: { ...booking.pricing, counter_offer: 1 },
            status: Status.PENDING,
          });
          return newBooking;
        });
        onClose();
      },
      onError: () => {
        toast.error(upperFirst(t('error:error')));
      },
    },
  );

  const handleFormSubmit = async ({
    counter_offer,
    from_date,
    message_for_tenant,
  }: BookingCounterOfferForm) => {
    try {
      mutateAsync({
        bookingId: booking.id,
        counter_offer: Number(counter_offer),
        from_date,
        message_for_tenant,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const { control, formState, handleSubmit } =
    useForm<BookingCounterOfferForm>();

  return (
    <Modal>
      <ModalBody>
        <ModalHeader
          title={upperFirst(t('booking:counterOffer'))}
          onClose={onClose}
        />
        <form
          id="booking_counter_offer_form"
          onSubmit={handleSubmit(handleFormSubmit)}
          noValidate
        >
          <ModalContent>
            <StyledGrid>
              <Controller
                control={control}
                name="counter_offer"
                rules={{
                  required: defaultFormErrorLabel,
                  pattern: {
                    value: numberValidationPattern,
                    message: upperFirst(t('formError:numberInput')),
                  },
                }}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <TextInput
                    id="counter-offer"
                    label={upperFirst(t('form:counterOffer'))}
                    name="counter-offer"
                    type="text"
                    value={value}
                    onChange={debounce(onChange, 300)}
                    showEuroIcon
                  >
                    {formState.errors.counter_offer && (
                      <FormNotice isWrapped={true}>{error?.message}</FormNotice>
                    )}
                  </TextInput>
                )}
              />
              <Controller
                control={control}
                name="from_date"
                rules={{
                  required: defaultFormErrorLabel,
                  validate: {
                    minDate: value => {
                      if (isBefore(new Date(value), new Date()))
                        return upperFirst(t('formError:minDate'));
                      return true;
                    },
                  },
                }}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <TextInput
                    id="from-date"
                    label={upperFirst(t('form:checkIn'))}
                    name="from-date"
                    type="date"
                    value={value}
                    onChange={debounce(onChange, 300)}
                    validations={{
                      min: format(addDays(new Date(), 1), 'yyyy-M-d'),
                    }}
                  >
                    {formState.errors.from_date && (
                      <FormNotice isWrapped={true}>{error?.message}</FormNotice>
                    )}
                  </TextInput>
                )}
              />
            </StyledGrid>
            <Controller
              control={control}
              name="message_for_tenant"
              rules={{
                required: defaultFormErrorLabel,
              }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <TextInput
                  id="message-for-tenant"
                  label={upperFirst(t('form:messageForTenant'))}
                  name="message-for-tenant"
                  type="text"
                  value={value}
                  onChange={debounce(onChange, 300)}
                >
                  {formState.errors.message_for_tenant && (
                    <FormNotice isWrapped={true}>{error?.message}</FormNotice>
                  )}
                </TextInput>
              )}
            />
          </ModalContent>
          <ModalFooter>
            <StyleButtonWrapper>
              <StyledButton
                user={user?.type}
                variant="secondary"
                onClick={onClose}
              >
                {upperFirst(t('common:cancel'))}
              </StyledButton>
              <StyledButton
                user={user?.type}
                type="submit"
                disabled={isLoading}
              >
                {isLoading && (
                  <StyledSpinner
                    foregroundColor={theme.colors.cross.ultralightGray}
                    size={15}
                    strokeSize={2}
                  />
                )}
                {upperFirst(t('common:send'))}
              </StyledButton>
            </StyleButtonWrapper>
          </ModalFooter>
        </form>
      </ModalBody>
    </Modal>
  );
};

export default BookingCounterOfferModal;
